import { isBefore, isWithinInterval, startOfWeek } from "date-fns";

/**
 * Gets the localStorage key for tracking spending limit increase requests.
 */
export function getRequestStorageKey(workplaceId: string, weekStartDate: Date): string {
  return `spending-limit-request-${workplaceId}-${weekStartDate.toISOString().split("T")[0]}`;
}

/**
 * Gets the Sunday (start of week) for a given date.
 */
export function getWeekStartForDate(date: Date): Date {
  return startOfWeek(date, { weekStartsOn: 0 });
}

/**
 * Saves a spending limit increase request to localStorage.
 * MVP implementation - in the future this could POST to a Slack webhook or internal API.
 */
export function saveSpendingLimitIncreaseRequest(
  workplaceId: string,
  weekStartDate: Date,
  additionalHours: number
): void {
  const key = getRequestStorageKey(workplaceId, weekStartDate);
  const request = {
    requestedAt: new Date().toISOString(),
    additionalHours,
  };
  try {
    localStorage.setItem(key, JSON.stringify(request));
  } catch {
    // No-op for MVP; localStorage may fail due to quota/security/privacy modes
  }
}

/**
 * Checks if a spending limit increase request has been submitted for a given week.
 * Returns true if a request exists and the spending control hasn't been updated since the request.
 *
 * @param workplaceId - The workplace ID
 * @param weekStartDate - The start date of the week (Sunday)
 * @param spendingControlUpdatedAt - The updatedAt timestamp from the spending control (or createdAt if no updates)
 */
export function hasSpendingLimitIncreaseRequest(
  workplaceId: string,
  weekStartDate: Date,
  spendingControlUpdatedAt: Date | undefined
): boolean {
  const key = getRequestStorageKey(workplaceId, weekStartDate);

  try {
    const stored = localStorage.getItem(key);

    if (!stored) {
      return false;
    }

    const request = JSON.parse(stored) as { requestedAt: string };
    const requestedAt = new Date(request.requestedAt);

    // If there's no spending control for this week, the request is still pending
    if (!spendingControlUpdatedAt) {
      return true;
    }

    // Request is pending if it was made after the spending control was last updated
    return requestedAt > spendingControlUpdatedAt;
  } catch {
    return false;
  }
}

interface ShiftWithStartDate {
  start: Date;
}

/**
 * Gets all unique week start dates (Sundays) that contain at least one shift.
 * Uses the shift's start date to determine which week it belongs to.
 *
 * @param shifts - Array of shifts with start dates
 * @returns Array of unique week start dates (Sundays)
 */
export function getWeekStartDatesContainingShifts(shifts: ShiftWithStartDate[]): Date[] {
  if (shifts.length === 0) {
    return [];
  }

  // Get min and max shift start dates
  const shiftStartDates = shifts.map((shift) => shift.start);
  const minDate = shiftStartDates.reduce((min, date) => (isBefore(date, min) ? date : min));
  const maxDate = shiftStartDates.reduce((max, date) => (isBefore(max, date) ? date : max));

  // Generate all weeks from min to max
  const weeks: Date[] = [];
  let currentWeekStart = getWeekStartForDate(minDate);
  const lastWeekStart = getWeekStartForDate(maxDate);

  while (!isBefore(lastWeekStart, currentWeekStart)) {
    // Capture current week boundaries for the check
    const weekStart = currentWeekStart;
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const hasShiftInWeek = shifts.some((shift) =>
      isWithinInterval(shift.start, { start: weekStart, end: weekEnd })
    );

    if (hasShiftInWeek) {
      weeks.push(weekStart);
    }

    // Move to next week
    currentWeekStart = new Date(currentWeekStart);
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }

  return weeks;
}
